import React, { useEffect, useState, startTransition, useCallback } from 'react';
import { Table, Button, Space, Form, Input, Card, Row, Col, message, Tag, Typography, Pagination, Select, Modal, Empty } from 'antd';
import { SearchOutlined, PlusOutlined, EyeOutlined, TagsOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { getInventoryConfigList, type InventoryConfig } from '../../../services/inventory';
import { getSortingGroupTypeTagColor, getSortingGroupBreakdownPositive } from '../../../utils/sortingGroupTypeTag';
import type { ListResponse } from '../../../types/common';
import { useTableQuery } from '../../../hooks/useTableQuery';
import { useTabStateManagement } from '../../../hooks/useTabStateManagement';
import { useAuth } from '../../../hooks/useAuth';
import { usePermissions } from '../../../contexts/PermissionContext';
import request from '../../../utils/request';
import dayjs from 'dayjs';

const { Title } = Typography;

interface InventoryConfigFilters {
  project_code: string;
  project_name: string;
  customer?: string;
  project_type?: string; // 新增：项目类型筛选
}

const InventoryConfigList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { hiddenFields } = usePermissions();
  const [form] = Form.useForm();
  const [searching, setSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [customers, setCustomers] = useState<Array<{ id: string; username: string }>>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [sortingGroupModalRecord, setSortingGroupModalRecord] = useState<InventoryConfig | null>(null);

  
  
  // 使用 useTableQuery Hook - 实现"单一数据源 + 强制同步 + 解耦触发"架构
  const {
    state,
    data,
    loading,
    error,
    actions,
    paginationConfig
  } = useTableQuery<InventoryConfigFilters>({
    initialState: {
      pagination: { current: 1, pageSize: 20, total: 0 },
      filters: { project_code: '', project_name: '', customer: '', project_type: '' },
      sorting: { field: 'created_at', order: 'desc' }
    },
    urlConfig: {
      syncToUrl: true,           // 启用URL状态同步
      urlKeys: ['pagination', 'filters', 'sorting']  // 同步分页、筛选、排序到URL
    },
    fetchData: async (state) => {
      const { pagination, filters } = state;

      // 构建API参数
      const hidden = hiddenFields('inventory.ProjectInventoryConfig');
      const hideCustomer = Array.isArray(hidden) && hidden.includes('project__customer__username');

      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
        project_code: filters.project_code || '',
        project_name: filters.project_name || '',
        customer: hideCustomer ? '' : (filters.customer || ''),
        project_type: filters.project_type || '', // 添加项目类型参数
      };

      // 调用API
      const response = await getInventoryConfigList(params);

      return {
        results: response.data.results,
        count: response.data.count
      };
    }
  });

  // 当策略要求隐藏客户时，清空客户筛选
  useEffect(() => {
    const hidden = hiddenFields('inventory.ProjectInventoryConfig');
    const hideCustomer = Array.isArray(hidden) && hidden.includes('project__customer__username');
    if (hideCustomer && (state.filters.customer || '') !== '') {
      actions.updateFilters({ customer: '' });
    }
  }, [hiddenFields, actions, state.filters.customer]);

  // 使用 useTabStateManagement 管理页面特定状态（sessionStorage）
  const { saveState, restoreState } = useTabStateManagement({
    keepState: true,
    persistToStorage: true
  });

  // 表格变化处理（分页、排序等）
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {

    // 处理分页变化
    if (pagination.current !== state.pagination.current) {
      actions.updatePagination({ current: pagination.current });
    }
    
    if (pagination.pageSize !== state.pagination.pageSize) {
      actions.updatePagination({ current: 1, pageSize: pagination.pageSize });
    }
    
    // 处理排序变化
    if (sorter && sorter.field) {
      console.log('排序变化:', sorter);
      actions.updateSorting({
        field: sorter.field,
        order: sorter.order === 'ascend' ? 'asc' : 'desc'
      });
    }
  };

  // 搜索处理
  const handleSearch = () => {
    const values = form.getFieldsValue();

    // 过滤空值
    const filters = {
      project_code: values.project_code?.trim() || '',
      project_name: values.project_name?.trim() || '',
      customer: values.customer || '',
      project_type: values.project_type || '', // 添加项目类型筛选
    };

    // 更新筛选条件
    actions.updateFilters(filters);
    
    // 搜索时重置到第一页
    actions.updatePagination({ current: 1 });
  };

  // 实时搜索处理函数
  const handleRealTimeSearch = useCallback((field: 'project_code' | 'project_name', value: string) => {

    // 清除之前的定时器
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // 设置搜索状态
    setSearching(true);
    
    // 创建新的定时器，500ms后执行搜索
    const timeout = setTimeout(() => {
      const currentValues = form.getFieldsValue();
      
      // 构建搜索条件（保持客户筛选）
      const filters = {
        project_code: field === 'project_code' ? value.trim() : (currentValues.project_code?.trim() || ''),
        project_name: field === 'project_name' ? value.trim() : (currentValues.project_name?.trim() || ''),
        customer: currentValues.customer || '',
        project_type: currentValues.project_type || '', // 添加项目类型筛选
      };

      // 更新筛选条件
      actions.updateFilters(filters);
      
      // 重置到第一页
      actions.updatePagination({ current: 1 });
      
      // 清除搜索状态
      setSearching(false);
    }, 500);
    
    setSearchTimeout(timeout);
  }, [searchTimeout, form, actions]);

  // 处理输入框变化
  const handleInputChange = useCallback((field: 'project_code' | 'project_name') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // 如果输入框为空或长度大于等于2个字符，触发实时搜索
    if (value === '' || value.length >= 2) {
      handleRealTimeSearch(field, value);
    } else if (value.length === 1) {
      // 如果只有1个字符，清除搜索状态但不触发搜索
      setSearching(false);
      if (searchTimeout) {
        clearTimeout(searchTimeout);
        setSearchTimeout(null);
      }
    }
  }, [handleRealTimeSearch, searchTimeout]);

  // 重置处理
  const handleReset = () => {
    form.resetFields();
    
    // 重置筛选条件到初始状态
    const initialFilters = { project_code: '', project_name: '', customer: '', project_type: '' };
    actions.updateFilters(initialFilters);
    
    // 重置分页到第一页
    actions.updatePagination({ current: 1 });
  };

  // 客户筛选变化处理（实时搜索）
  const handleCustomerChange = useCallback((value: string | undefined) => {

    // 获取当前表单的其他值
    const currentValues = form.getFieldsValue();
    
    // 构建新的筛选条件
    const filters = {
      project_code: currentValues.project_code?.trim() || '',
      project_name: currentValues.project_name?.trim() || '',
      customer: value || '',
      project_type: currentValues.project_type || '', // 添加项目类型筛选
    };

    // 更新筛选条件
    actions.updateFilters(filters);
    
    // 重置到第一页
    actions.updatePagination({ current: 1 });
  }, [form, actions]);

  

  // 获取客户列表（仅主账号）
  const fetchCustomers = async () => {
    if (loadingCustomers) return;
    setLoadingCustomers(true);
    try {
      const response = await request.get('/api/v1/users/', {
        params: { 
          role: 'customer',
          sub_role: 'main_account',
          page_size: 1000 
        }
      });
      
      // 根据API响应结构处理数据
      let customerList: any[] = [];
      if (response.data && Array.isArray(response.data)) {
        // 如果直接返回数组
        customerList = response.data;
      } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
        // 如果返回分页结构
        customerList = response.data.results;
      }

      // 确保数据格式正确
      const formattedCustomers = customerList.map((customer: any) => ({
        id: customer.id?.toString() || customer.id,
        username: customer.username || customer.name || '未知客户'
      }));

      setCustomers(formattedCustomers);
    } catch (error) {
      message.error('获取客户列表失败');
    } finally {
      setLoadingCustomers(false);
    }
  };

  // 根据用户角色生成正确的URL前缀
  const getUrlPrefix = () => {
    if (location.pathname.includes('/dashboard/staff')) {
      return '/dashboard/staff/projects/inventory-config';
    } else if (location.pathname.includes('/dashboard/admin')) {
      return '/dashboard/admin/projects/inventory-config';
    } else {
      // 客户路径统一到 /dashboard/customer/ 前缀
      return '/dashboard/customer/projects/inventory-config';
    }
  };

  // 操作处理函数
  const handleView = (record: InventoryConfig) => {
    const urlPrefix = getUrlPrefix();
    // 先写入系统编码到 sessionStorage，确保 TabsManager 首次创建标签即可读取
    try {
      const basePath = `${urlPrefix}/${record.id}`;
      const sysCode = (record as any).project_code || (record as any).project?.project_code || '';
      if (sysCode) {
        sessionStorage.setItem(`tab_title_code:${basePath}`, sysCode);
      }
    } catch (_) {}
    startTransition(() => navigate(`${urlPrefix}/${record.id}`));
  };

  const handleCreate = () => {
    const urlPrefix = getUrlPrefix();
    startTransition(() => navigate(`${urlPrefix}/create`));
  };

  const handleEdit = (record: InventoryConfig) => {
    const urlPrefix = getUrlPrefix();
    try {
      const editPath = `${urlPrefix}/${record.id}/edit`;
      const detailPath = `${urlPrefix}/${record.id}`;
      const projectCode = (record as any).project_code || (record as any).project?.project_code || '';
      if (projectCode) {
        sessionStorage.setItem(`tab_title_code:${editPath}`, projectCode);
        sessionStorage.setItem(`tab_title_code:${detailPath}`, projectCode);
      }
    } catch (_) {}
    startTransition(() => navigate(`${urlPrefix}/${record.id}/edit`));
  };

  // 查看库存单元标签
  const handleViewLabels = (record: InventoryConfig) => {
    const urlPrefix = getUrlPrefix();
    startTransition(() => navigate(`${urlPrefix}/${record.id}/labels`));
  };

  // 渲染分拣组信息
  const renderSortingGroups = (groups: InventoryConfig['sorting_groups'], type: 'LOOSE' | 'BOX' | 'PALLET') => {
    const group = groups.find(g => g.group_type === type);
    if (!group) return '-';
    return (
      <Space direction="vertical" size="small">
        <span>{group.name}</span>
        <Tag color="blue">库存单元: {group.inventory_unit_count}</Tag>
      </Space>
    );
  };

     const columns = [
     {
       title: '项目编码',
       dataIndex: 'project_code',
       key: 'project_code',
       width: 120,
     },
    {
      title: '项目名称',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 200,
    },
    {
      title: '项目类型',
      dataIndex: 'project_type_display',
      key: 'project_type',
      width: 120,
    },
    {
      title: '分拣组',
      key: 'sorting_groups',
      width: 120,
      align: 'center' as const,
      render: (_: any, record: InventoryConfig) => (
        <Tag
          color="blue"
          className="cursor-pointer select-none m-0"
          onClick={() => setSortingGroupModalRecord(record)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setSortingGroupModalRecord(record);
            }
          }}
        >
          {record.inventory_summary.total_groups}
        </Tag>
      ),
    },
    {
      title: '库存单元',
      key: 'inventory_units',
      width: 120,
      render: (_: any, record: InventoryConfig) => (
        <Tag color="green">
          {record.inventory_summary.total_units}
        </Tag>
      ),
    },
    {
      title: 'SKU种类',
      key: 'sku_count',
      width: 120,
      render: (_: any, record: InventoryConfig) => (
        <Tag color="purple">{record.sku_count}</Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 300,
      render: (_: any, record: InventoryConfig) => (
        <Space size="middle">
                     <Button
             type="link"
             icon={<TagsOutlined />}
             onClick={() => handleViewLabels(record)}
           >
             查看库存单元标签
           </Button>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            查看详情
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑配置
          </Button>
        </Space>
      ),
    },
  ];

  // 当filters变化时，更新表单的初始值
  useEffect(() => {
    if (state.filters) {
      form.setFieldsValue({
        project_code: state.filters.project_code || '',
        project_name: state.filters.project_name || '',
        customer: state.filters.customer || '',
        project_type: state.filters.project_type || '', // 添加项目类型筛选
      });
    }
  }, [state.filters, form]);

  

  // 组件加载时获取客户列表（仅对员工和管理员）
  useEffect(() => {
    const isAdminOrStaff = location.pathname.includes('/dashboard/staff') || location.pathname.includes('/dashboard/admin');
    const hidden = hiddenFields('inventory.ProjectInventoryConfig');
    const hideCustomer = Array.isArray(hidden) && hidden.includes('project__customer__username');

    if (isAdminOrStaff && !hideCustomer) {
      fetchCustomers();
    }
  }, [location.pathname, user?.role, hiddenFields]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  return (
    <div className="p-6">
             {/* 页面标题和统计信息 */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Title level={4} style={{ margin: 0 }}>项目基础配置</Title>
          <div className="flex items-center space-x-2">
            <Tag color="blue" style={{ fontSize: '14px', padding: '8px' }}>
              共 {state.pagination.total} 条记录
            </Tag>
          </div>
        </div>
      </div>

      {/* 筛选组件 */}
      <Card size="small" className="mb-4">
        <Form
          form={form}
          layout="horizontal"
          onFinish={handleSearch}
        >
                     <Row gutter={16}>
            <Col span={4}>
              <Form.Item name="project_code" label="项目编码">
                <Input 
                  placeholder="请输入项目编码" 
                  allowClear 
                  onPressEnter={handleSearch}
                  onChange={handleInputChange('project_code')}
                  suffix={searching ? <SearchOutlined spin /> : null}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="project_name" label="项目名称">
                <Input 
                  placeholder="请输入项目名称" 
                  allowClear 
                  onPressEnter={handleSearch}
                  onChange={handleInputChange('project_name')}
                  suffix={searching ? <SearchOutlined spin /> : null}
                />
              </Form.Item>
            </Col>
            {/* 客户筛选框 - 仅对员工和管理员显示，且未被敏感字段策略隐藏 */}
            {((location.pathname.includes('/dashboard/staff') || location.pathname.includes('/dashboard/admin')) &&
              !(Array.isArray(hiddenFields('inventory.ProjectInventoryConfig')) && hiddenFields('inventory.ProjectInventoryConfig').includes('project__customer__username'))) && (
              <Col span={4}>
                <Form.Item name="customer" label="所属客户">
                  <Select
                    placeholder="请选择客户"
                    allowClear
                    loading={loadingCustomers}
                    showSearch
                    optionFilterProp="children"
                    listHeight={320}
                    dropdownMatchSelectWidth={280}
                    onChange={handleCustomerChange}
                    notFoundContent={
                      <div>
                        <div>暂无客户数据</div>
                        <Button 
                          type="link" 
                          size="small" 
                          onClick={fetchCustomers}
                          loading={loadingCustomers}
                        >
                          重新加载
                        </Button>
                      </div>
                    }
                  >
                    {customers.map(customer => (
                      <Select.Option key={customer.id} value={customer.id}>
                        {customer.username}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
            <Col span={4}>
              <Form.Item name="project_type" label="项目类型">
                <Select
                  placeholder="请选择项目类型"
                  allowClear
                  onChange={(value) => {
                    const currentValues = form.getFieldsValue();
                    const filters = {
                      ...currentValues,
                      project_type: value || ''
                    };
                    actions.updateFilters(filters);
                    actions.updatePagination({ current: 1 });
                  }}
                >
                  <Select.Option value="">全部</Select.Option>
                  <Select.Option value="OrderFulfillment">一件代发</Select.Option>
                  <Select.Option value="StockTransfer">备货中转</Select.Option>
                  <Select.Option value="Relabeling">简单贴换标/清点</Select.Option>
                  <Select.Option value="ReturnProcessing">复杂退货/退仓处理</Select.Option>
                  <Select.Option value="Mixed">多种业务模式混合</Select.Option>
                  <Select.Option value="Other">其他</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                    loading={loading || searching}
                  >
                    搜索
                  </Button>
                  <Button onClick={handleReset}>
                    重置
                  </Button>
                </Space>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                  新建配置
                </Button>
              </Form.Item>
            </Col>
          </Row>
       </Form>
      </Card>
      
     {/* 数据表格 */}
     <Card>
       <Table
         columns={columns}
         dataSource={data}
         rowKey="id"
         loading={loading}
         scroll={{ x: 'max-content' }}
         pagination={false}
         onChange={handleTableChange}
       />
     </Card>
     
     {/* 分页组件 */}
     <div className="flex justify-end items-center mt-4">
       <Pagination {...paginationConfig} />
     </div>

      <Modal
        title={
          sortingGroupModalRecord
            ? `分拣组明细 — ${sortingGroupModalRecord.project_code || sortingGroupModalRecord.project_name || '项目'}`
            : '分拣组明细'
        }
        open={!!sortingGroupModalRecord}
        onCancel={() => setSortingGroupModalRecord(null)}
        destroyOnClose
        footer={
          <Button type="primary" onClick={() => setSortingGroupModalRecord(null)}>
            关闭
          </Button>
        }
      >
        {sortingGroupModalRecord ? (
          (() => {
            const items = getSortingGroupBreakdownPositive(sortingGroupModalRecord.inventory_summary);
            if (items.length === 0) {
              return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无有数量的分拣组类型" />;
            }
            return (
              <Space direction="vertical" size="middle" className="w-full">
                {items.map((item) => (
                  <Tag key={item.groupType} color={getSortingGroupTypeTagColor(item.groupType)} className="m-0 mr-0">
                    {item.label}: {item.count}组
                  </Tag>
                ))}
              </Space>
            );
          })()
        ) : null}
      </Modal>
   </div>
 );
};

export default InventoryConfigList;  